import {API} from "aws-amplify"

import {currentAuthenticatedUser} from "services/auth/hooks"
import {AppCenterApiName} from "services/constants"

import {
  clearLoginInfoByClientId,
  handleLineBinding,
  handleLoginRedirect,
  getCallbackClient,
  unregisterCallbackClient,
} from "services/auth/cache"
import {storeUserToken} from "./hooks"
import {generateClientCallbackUrl, shouldAppendToken} from "utils/auth"

/**
 * @typedef {{
 *   allowed: boolean
 * }} TGiantClientCallbackUrlResult
 */

/**
 * @returns {Promise<TGiantClientCallbackUrlResult>}
 */
export function searchCallbackUrl(clientId = "", callbackUri = "") {
  return API.get(
    AppCenterApiName.AppCenterApi,
    `/callback?clientId=${clientId}&callbackUri=${callbackUri}`,
    {},
  )
}

/**
 * redirect to client callback url, if not handled return false
 */
export async function handleClientIdRedirect() {
  const {id, uri, state} = getCallbackClient()
  if (!id || !uri) return false

  const cognitoUser = await currentAuthenticatedUser().catch(() => {
    throw new Error("user not logged in")
  })

  // TODO: replace API with the new one in 2.0
  const {allowed} = await searchCallbackUrl(id, uri).catch(() => {
    throw new Error("search callback url failed")
  })
  const currentSession = cognitoUser.getSignInUserSession()
  if (currentSession && allowed) {
    const tokens = {
      refreshToken: currentSession.getRefreshToken().getToken(),
      idToken: currentSession.getIdToken().getJwtToken(),
      accessToken: currentSession.getAccessToken().getJwtToken(),
    }

    const {code} = await storeUserToken(tokens).catch(() => {
      throw new Error("store user token failed")
    })

    unregisterCallbackClient()
    clearLoginInfoByClientId(id)

    let callbackUrl
    if (shouldAppendToken(id)) {
      callbackUrl = generateClientCallbackUrl({
        callbackUri: uri,
        code,
        tokens,
        state,
      })
    } else {
      callbackUrl = generateClientCallbackUrl({callbackUri: uri, code, state})
    }
    window.location.replace(callbackUrl)
    return true
  } else {
    return false
  }
}

/**
 * when user is logged in successfully (via password, mfa, social, ...),
 * need to decide where to redirect the user to
 * there're three cases:
 *   - line binding, (will close the in-app browser)
 *   - login redirect, (redirect to the url in the query string)
 *   - callback clientId,
 *
 * return true if redirect is handled, otherwise return false
 */
export async function redirectWhenAuthSuccess() {
  let lineBindingHandled = await handleLineBinding().catch(() => false)

  if (lineBindingHandled) return true

  if (handleLoginRedirect()) {
    return true
  }

  return await handleClientIdRedirect().catch(() => false)
}

/**
 *
 * @param {string} sub
 * @param {import('services/auth/types').TAvailableLoginWay} provider
 * @returns
 */
export const manualLinkAccount = (sub, provider) =>
  API.post(AppCenterApiName.AppCenterApiWithToken, "/account", {
    body: {sub, provider},
  })
